import React from 'react'
import {get, isEmpty, isNil, set, uniqBy} from 'lodash'
import * as pivotTableActions from 'actions/pivotTable'
import DateRender from 'containers/date'
import DateTimeRender from 'containers/dateTime'
import Dates from 'helpers/dates'
import ColorStatus from 'components/colorStatus'
import {isUUID} from 'helpers/uuid'
import {getIds} from 'helpers/general/lists'
import types from "actions/types"
import FinancialString from "containers/financialString"
import {getMargin, getPercentageString} from "helpers/financial"
import Translator from "components/translator"

const dispatchEdit = (dispatch, stateKey, key, entity, allowAutoSave = true) => {
  dispatch(pivotTableActions.editData(stateKey, key, entity, allowAutoSave && autoSave))
}
const dispatchSave = (dispatch, stateKey) => {
  dispatch(pivotTableActions.saveData(stateKey))
}

const sortDefaultColumns = (col1, col2) => {
  const order1 = col1.order || 0
  const order2 = col2.order || 0
  if (order1 > order2) return 1
  if (order1 < order2) return -1

  if (col1.label > col2.label) return 1
  if (col1.label < col2.label) return -1
  return 0
}

export const getVisibleColumns = (pivotTableState, columns) => {
  let ret = pivotTableState.columns
  if (isNil(ret) || ret.length === 0) {
    // Default visible columns:
    const sortedColumns = [...Object.keys(columns).map(k => ({...columns[k], key: k}))]
    sortedColumns.sort(sortDefaultColumns)
    ret = sortedColumns.filter(c => isNil(c.visible) || c.visible).map(c => c.key)
  } else {
    ret = ret.filter(c => columns[c]) // remove invalid columns
  }
  return ret
}

// Edit a range of data from the pivot table
// rangeData = an array of { entity, column } objects, as provided by EditablePivotTable.actions.onClick
// onEdit = a function (entity, column) which is called for each cell.
//          Use editEntity() to perform any edits and return true if you have edited something.
// onDispatch = a function (action) to call to dispatch actions to the store
// stateKey = the stateKey of the pivot table
// autoSave = if true, will save the changes immediately
export const rangeEdit = ({rangeData, onEdit, onDispatch, stateKey, autoSave}) => {
  const changes = {}
  rangeData.forEach(({entity, column}) => {
    if (onEdit(entity, column)) changes[entity.id] = entity
  })

  const changedKeys = Object.keys(changes)
  if (changedKeys.length > 0) {
    changedKeys.forEach(key => {
      dispatchEdit(onDispatch, stateKey, key, changes[key], false)
    })
    if (autoSave) dispatchSave(onDispatch, stateKey)
  }
}

// Edit an array of data from the pivot table
// data = an array of data, as provided by EditablePivotTable.bulkActions.onClick
// onEdit = a function (data) which is called for each data row.
//          Use editData() to perform any edits and return true if you have edited something.
// onDispatch = a function (action) to call to dispatch actions to the store
// stateKey = the stateKey of the pivot table
// autoSave = if true, will save the changes immediately
export const dataEdit = ({data, onEdit, onDispatch, stateKey, autoSave}) => {
  const changes = {}
  data.forEach(entity => {
    if (onEdit(entity)) changes[entity.id] = entity
  })

  const changedKeys = Object.keys(changes)
  if (changedKeys.length > 0) {
    changedKeys.forEach(key => {
      dispatchEdit(onDispatch, stateKey, key, changes[key], false)
    })
    if (autoSave) dispatchSave(onDispatch, stateKey)
  }
}

// Edit a specific column in a specific entity
// entity = the entity to edit
// column = the column definition you are editing
// value = the new value of the column
export const editEntity = ({entity, column, value}) => {
  const edited = column.setData(entity, value)
  setEntityEdited(entity, column.key)
  return edited
}

// Edit a specific column in a specific entity
// entity = the entity to edit
// prop = the property of the entity to edit
// value = the new value of the column
// columnKey = the key of the column that was edited (if different to prop)
export const editData = ({entity, prop, value, columnKey = null}) => {
  if (columnKey === null) columnKey = prop

  set(entity, prop, value)
  setEntityEdited(entity, columnKey)
}

export const setEntityEdited = (entity, columnKey) => {
  if (!isNil(columnKey)) {
    entity._editedKeys = {
      ...entity._editedKeys,
      [columnKey]: true,
    }
  }
}

export const editDataMulti = ({entities, values, where, columnKey}) => {
  return entities.filter(where).map(c => {
    values.forEach(v => {
      editData({
        entity: c,
        prop: v.prop,
        value: v.value,
        columnKey,
      })
    })

    return c
  })
}

export const createDateTimeColumn = (definition) => ({
  ...definition,
  data: e => {
    // render date/time as per locale settings
    const date = definition.data(e)
    if (date) {
      if (definition.customRender) return definition.customRender(e)
      let showTime = true
      if (definition.showTime) showTime = definition.showTime(e)
      if (showTime) return <DateTimeRender>{date}</DateTimeRender>
      return <DateRender>{date}</DateRender>
    }
  },
  group: e => {
    // group using just the date value
    const date = definition.data(e)
    if (date) return Dates.getDateString(date)
  },
  groupData: e => {
    // render group header using date part as per locale settings
    const date = definition.data(e)
    if (date) return <DateRender>{date}</DateRender>
  },
  export: e => {
    // export the entire date/time
    const date = definition.data(e)
    if (date) {
      let showTime = true
      if (definition.showTime) showTime = definition.showTime(e)
      if (showTime) return Dates.getDateTimeString(date)
      return Dates.getDateString(date)
    }
  },
})

export const createDateColumn = (definition) => createDateTimeColumn({...definition, showTime: () => false})

export const isSearching = state => !isNil(state.search) && state.search.length > 0

export const isFiltering = filters => filters.length > 0 &&
  filters.some(filter => {
    const filterKey = Object.keys(filter)[0]
    return filter[filterKey] === true
  })

export const mapFilterValue = (filterKey, filterValue) => {
  if (filterKey === 'type') return filterValue !== -1
  if (filterKey === 'creatorId') return !isNil(filterValue)
  if (filterKey === 'estimatorId') return !isNil(filterValue)
  if (filterKey === 'salesmanId') return !isNil(filterValue)
  if (filterKey === 'timeKey') return filterValue !== 'month:0'
  if (isNil(filterValue)) return false
  return filterValue
}

export const mapFilterObject = (filterKey, filters) => {
  const filterObject = {}
  filterObject[filterKey] = mapFilterValue(filterKey, filters[filterKey])
  return filterObject
}

export const convertObjectToArray = state => {
  const filters = state.filters
  return Array.isArray(filters)
    ? filters
    : Object
      .keys(filters)
      .map(filterKey => mapFilterObject(filterKey, filters))
}

export const getSelectedEntities = (entities = []) => {
  return entities.filter(entity => entity.selected)
}

export const isBulkActionsEnabled = (entities = []) => {
  return entities.some(entity => entity.selected)
}

export const getBulkActionLabel = (action, entityName, entities) => {
  const totalEntities = entities.length
  let label = ''
  if (totalEntities === 0) label = ` ${entityName}s`
  if (totalEntities === 1) label = ` 1 ${entityName}`
  if (totalEntities > 1) label = ` ${totalEntities} ${entityName}s`

  return `${action}${label}`
}

export const getGroupData = (e, options = [], idKey, optionKey, optionValue = 'name') => {
  const id = get(e, idKey, null)
  const option = options.find(option => get(option, optionKey, null) === id)
  return isNil(option)
    ? null
    : get(option, optionValue, null)
}

export const getMultiGroupData = (ids, options = [], optionValue = 'name') => {
  if (isEmpty(ids))
    return null

  const items = ids.map(id => {
    const item = options.find(item => item.id === id)
    return isNil(item)
      ? null
      : get(item, optionValue, null)
  })

  return items.filter(item => !isNil(item)).join(', ')
}

export const getMultiGroupDataForKey = (e, idKey, optionValue = 'name') => {
  let items = get(e, idKey, [])
  items = isNil(items)
    ? []
    : uniqBy(items, 'id').map(item => {
      return get(item, optionValue, null)
    })

  return isEmpty(items)
    ? null
    : items.join(', ')
}

export const getColumnOptions = (e, column) => {
  return typeof column.options === 'function'
    ? column.options(e)
    : column.options
}

export const getColorStatusOption = (backgroundColor, title) => {
  return (
    <Translator text={{title}} onRender={({title}) => (
      <div className={'status'} style={{alignItems: 'center', display: 'flex'}}>
        <ColorStatus backgroundColor={backgroundColor}/>
        <span className={'status-label'}>
          {title}
        </span>
      </div>
    )}/>
  )
}

export const areAllRowsSelected = (allRows, selectedRows) => {
  return !isNil(selectedRows) && !isEmpty(allRows) && allRows.length === selectedRows.length
}

export const getSelectedRows = (allRows, selectedRowIds) => {
  return allRows.filter(row => selectedRowIds.includes(row.id))
}

const isColumnVisible = (key, visibleColumns) => visibleColumns.includes(key)

export const applyColumnVisibility = (columns, visibleColumns) => {
  const adjustedColumns = {...columns}
  Object.values(adjustedColumns).forEach(column => {
    column.visible = isColumnVisible(column.key, visibleColumns)
  })
  return adjustedColumns
}

export const applyColumnSizes = (columns, columnSizes) => {
  if (isNil(columnSizes)) return columns

  const adjustedColumns = {...columns}
  Object.values(adjustedColumns).forEach(column => {
    column.size = columnSizes[column.key] || column.size
  })
  return adjustedColumns
}

export const getItemIdForUpdate = (item) => {
  return isUUID(item.id) ? -1 : item.id
}

export const getNonDeletedItems = (items) => {
  return isEmpty(items)
    ? []
    : items.filter(item => !item.deleted)
}

export const isRowSelected = (entity, selectedRows) => {
  const selectedRowIds = getIds(selectedRows)
  return selectedRowIds.includes(entity.id)
}
export const getPivotTableTotalReducer = (state, action, key) => {
  switch (action.type) {
    case types.PIVOT_TABLE_LOAD_SUCCESS:
      return action.key === key
        ? get(action, 'response.total', null)
        : state
    default:
      return state
  }
}

export const getPivotTableTotalPriceReducer = (state, action, key) => {
  switch (action.type) {
    case types.PIVOT_TABLE_LOAD_SUCCESS:
      return action.key === key
        ? get(action, 'response.totalPrice', null)
        : state
    default:
      return state
  }
}

export const getPivotTableTotalMarginReducer = (state, action, key) => {
  switch (action.type) {
    case types.PIVOT_TABLE_LOAD_SUCCESS:
      return action.key === key
        ? get(action, 'response.totalMargin', null)
        : state
    default:
      return state
  }
}

export const getPivotTableTotalCostReducer = (state, action, key) => {
  switch (action.type) {
    case types.PIVOT_TABLE_LOAD_SUCCESS:
      return action.key === key
        ? get(action, 'response.totalCost', null)
        : state
    default:
      return state
  }
}

export const getBooleanLabel = (value, trueLabel = 'Yes', falseLabel = 'No') => {
  return value
    ? trueLabel
    : falseLabel
}

export const getGroupOptionLabels = (groupsOptions) => {
  if (isEmpty(groupsOptions))
    return {}

  const groupOptionLabels = {}
  groupsOptions.forEach(groupOption => groupOptionLabels[groupOption.id] = groupOption.name)
  return groupOptionLabels
}

export const getGroupOptionsGroupData = (e, groupOptionLabels, optionIdsKey, optionsKey) => {
  const groupIds = get(e, optionIdsKey, [])
  let groups = get(e, optionsKey, [])
  groups = isEmpty(groups) ? [] : groups.map(group => group.id)

  const options = isEmpty(groupIds)
    ? groups
    : groupIds

  const groupIdOptions = [...new Set(options.map(groupId => groupOptionLabels[groupId]))]
  return groupIdOptions.join(', ')
}

export const getGroupColumnLabel = (column, groupData, visibleTotalGroupFields) => {
  const key = get(column, 'key', null)
  const currencyId = get(column, 'currencyId', null)
  const visibleTotalGroupField = visibleTotalGroupFields.find(totalField => key === get(totalField, 'label', null))
  if (!isNil(visibleTotalGroupField)) {
    const visibleTotalGroupFieldKey = get(visibleTotalGroupField, 'key', null)
    const groupFieldValue = `groupTotal_${visibleTotalGroupFieldKey}`
    if (visibleTotalGroupFieldKey === 'margin')
      return getPercentageString(groupData[groupFieldValue], 2)

    return (
      <FinancialString
        value={groupData[groupFieldValue]}
        currencyId={currencyId}
      />
    )
  }
  return ''
}

export const getTotalValue = (groupData, key) => {
  return groupData.data.reduce((sum, value) => {
    return sum + value[key]
  }, 0)
}

export const getTotalMargin = (groupData) => {
  const sumOfTotalPrice = groupData.data.reduce((sum, value) => {
    return sum + (value['homePrice'])
  }, 0)

  const sumOfTotalCost = groupData.data.reduce((sum, value) => {
    return sum + (value['totalCost'])
  }, 0)

  return getMargin(sumOfTotalPrice, sumOfTotalCost)
}

export const addVisibleTotalColumnToGroupData = (groupData, visibleTotalGroupFields) => {
  Object.values(groupData).forEach(data => {
    visibleTotalGroupFields.map(totalField => {
      const key = get(totalField, 'key', null)
      const sum = key === 'margin' ? getTotalMargin(data) : getTotalValue(data, key)
      return data[`groupTotal_${key}`] = sum
    })
  })
  return groupData
}

export const applyVisibleAndOrderColumns = (columns, visibleColumns) => {
  let order = 1

  Object.keys(columns).forEach(columnKey => {
    columns[columnKey].visible = false
  })

  visibleColumns.forEach(columnKey => {
    columns[columnKey].visible = true
    columns[columnKey].order = order++
  })

  return columns
}

export const getTranslatedColumns = (columns, translatedLabels) => {
  const translatedColumns = {}

  Object.keys(columns).forEach(columnKey => {
    //This is for checking custom user columns and returning the original or translated column label.
    if (columnKey.startsWith('user')) {
      translatedColumns[columnKey] = columns[columnKey]
    } else {
      translatedColumns[columnKey] = {
        ...columns[columnKey],
        label: translatedLabels[columnKey],
      }
    }
  })

  return translatedColumns
}

export const getTranslatedLabels = (columns) => {
  const text = {}

  Object.keys(columns).forEach(columnKey => {
    text[columnKey] = columns[columnKey].label
  })

  return text
}

export const getRowIds = (rows) => {
  if (isNil(rows)) return []
  const ids = rows.map(row => row.id)
  return !isNil(ids) ? ids : []
}

export const getFilteredDataBasedOnSearch = (data, search) => {
  return data.filter(item => item.name.toLowerCase().includes(search.toLowerCase()))
}

export const getDefaultVisibleColumns = (columns) => {
  return Object.keys(columns).filter(key => columns[key].visible)
}
